import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import classnames from "classnames";
import {toast} from "react-toastify";
import Page from "../Components/Page";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import {makeApolloClientCall, makeApolloClientMutationCall} from "../../Api/makeApolloApiCall";
import {toastError, toastSuccess} from "../../utils/toast";
import ReactTable from "../Components/ReactTable";
import EditableReactTableV2 from "../Components/ReactTable/EditableReactTableV2";
import {EditFormNumberInputField} from "../Components/ReactTable/EditRecordForm";
import {getStringParamOrDefault} from "../../utils/urlBuilder";
import {convertValueToCentral} from "../../utils/formatUtil";
import {buildSalesAnchorRows} from "./salesAnchorRows";
import UnlinkSourceStoreModal from "./Components/UnlinkSourceStoreModal";
import LinkSourceStoreModal from "./Components/LinkSourceStoreModal";
import {manualLinksForStore, salesHistoryForStore} from "./Components/storeLinkage";
import {formattedCell, formatValue, NOT_AVAILABLE} from "./Components/storeLinkageFormatters";
import StoreLinkageSalesHistory from "./Components/StoreLinkageSalesHistory";
import IconImage, {Icons} from "../Components/IconImage";

const toFieldTestId = (label) => `store-linkage-value-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '')}`;

export const STORE_LINKAGE_QUERY = `
query storeLinkage($canonicalStoreId: String) {
    storeLinkage(canonicalStoreId: $canonicalStoreId) {
        canonicalStoreId
        version
        sourceCount
        memberCount
        winningSource
        winningSourceStoreId
        bannerName
        address
        city
        state
        postalCode
        openDate
        closedDate
        evidenceTruncated
        salesAnchors {
            acvAnchor
            year
        }
        members {
            id
            source
            sourceStoreId
            survivorshipRank
            isWinner
            contributedFields
            bannerName
            address
            city
            state
            postalCode
            openDate
            closedDate
            lat
            lon
            updatedAt
        }
        matchEvidence {
            leftSourceRecordId
            rightSourceRecordId
            nameSimilarity
            addressSimilarity
            distanceMeters
        }
    }
}`;

export const UPDATE_SALES_ANCHOR_MUTATION = `
mutation updateSalesAnchor($canonicalStoreId: String, $year: Int!, $acvAnchor: Float!) {
    updateSalesAnchor(canonicalStoreId: $canonicalStoreId, year: $year, acvAnchor: $acvAnchor) {
        canonicalStoreId
        version
        year
        acvAnchor
        created
        salesAnchors {
            acvAnchor
            year
        }
    }
}`;

export const SALES_HISTORY_ERROR = 'Unable to load sales history for these records.';

const formatSimilarity = (value) =>
    (value === null || value === undefined) ? NOT_AVAILABLE : value.toFixed(3);

const formatDistance = (value) =>
    (value === null || value === undefined) ? NOT_AVAILABLE : `${value.toFixed(0)} m`;

const formatCityStateZip = ({city, state, postalCode}) =>
    [city, state, postalCode].filter(Boolean).join(', ') || NOT_AVAILABLE;

const formatCoordinates = ({lat, lon}) =>
    (lat === null || lat === undefined || lon === null || lon === undefined)
        ? NOT_AVAILABLE
        : `${lat.toFixed(5)}, ${lon.toFixed(5)}`;

const humanizeField = (field) =>
    field.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());

const formatContributedFields = (fields) =>
    (fields && fields.length) ? fields.map(humanizeField).join(', ') : '—';

const MEMBER_COLUMNS = [
    {accessorKey: "survivorshipRank", header: "#", size: 60},
    {
        accessorKey: "source",
        header: "SOURCE",
        size: 250,
        cell: ({row}) => <>
            {row.original.source}
            {row.original.isWinner &&
                <span className={"linkage-winner-badge"} data-testid={"linkage-winner-badge"}>WINNER</span>}
        </>
    },
    {accessorKey: "sourceStoreId", header: "STORE ID", size: 250, cell: formattedCell},
    {accessorKey: "id", header: "SOURCE RECORD ID", size: 300, cell: formattedCell},
    {accessorKey: "bannerName", header: "BANNER", size: 224, cell: formattedCell},
    {accessorKey: "address", header: "ADDRESS", size: 240, cell: formattedCell},
    {id: "cityStateZip", header: "CITY/STATE/ZIP", size: 210, accessorFn: formatCityStateZip},
    {accessorKey: "openDate", header: "OPEN", size: 120, cell: formattedCell},
    {accessorKey: "closedDate", header: "CLOSED", size: 120, cell: formattedCell},
    {id: "coordinates", header: "LAT/LON", size: 200, accessorFn: formatCoordinates},
    {
        accessorKey: "updatedAt", header: "UPDATED", size: 160, cell: ({getValue}) => {
            const value = getValue();
            if (value === null || value === undefined || value === '') return NOT_AVAILABLE;
            const centralDate = convertValueToCentral(Number(value));
            return centralDate.isValid() ? centralDate.format("YYYY-MM-DD HH:mm") : formatValue(value);
        }
    },
    {
        accessorKey: "contributedFields",
        header: "CONTRIBUTED",
        size: 300,
        cell: ({row}) => <span data-testid={`linkage-member-contributed-${row.original.id}`}>
            {formatContributedFields(row.original.contributedFields)}
        </span>
    },
];

const ANCHOR_COLUMNS = [
    {accessorKey: "year", header: "YEAR", size: 150, cell: formattedCell},
    {
        accessorKey: "acvAnchor",
        header: "ACV ANCHOR",
        size: 200,
        cell: ({row}) => row.original.isPlaceholder
            ? <span className={"linkage-placeholder-badge"}
                    data-testid={"linkage-placeholder-badge"}>NO ANCHOR</span>
            : formatValue(row.original.acvAnchor)
    },
];

const validateAnchorPreSave = ({newData, form}) => {
    if (!(newData.acvAnchor > 0)) {
        form.setError('acvAnchor', {type: 'custom', message: ['ACV Anchor must be greater than zero']});
        return false;
    }
    return true;
};

const anchorTrProps = (row) => ({
    className: classnames("rt-tr", row.index % 2 === 0 ? "-odd" : "-even", "linkage-anchor-row",
        {placeholder: row.original.isPlaceholder}),
    "data-testid": `linkage-anchor-row-${row.original.year}`
});

const evidenceRowId = ({leftSourceRecordId, rightSourceRecordId}) =>
    `${leftSourceRecordId}-${rightSourceRecordId ?? 'manual'}`;

const manualRowsWithoutEvidence = ({members, matchEvidence, manualLinks}) => {
    const withEvidence = new Set(matchEvidence.flatMap(({leftSourceRecordId, rightSourceRecordId}) =>
        [leftSourceRecordId, rightSourceRecordId]));
    return members
        .filter(({id}) => manualLinks.has(id) && !withEvidence.has(id))
        .map(({id}) => ({
            leftSourceRecordId: id,
            rightSourceRecordId: null,
            nameSimilarity: null,
            addressSimilarity: null,
            distanceMeters: null,
        }));
};

const EVIDENCE_COLUMNS = [
    {accessorKey: "leftSourceRecordId", header: "RECORD A", size: 300},
    {
        id: "rightSourceRecordId",
        header: "RECORD B",
        size: 300,
        accessorFn: ({rightSourceRecordId}) => rightSourceRecordId ?? 'No match evidence'
    },
    {
        id: "nameSimilarity",
        header: "BANNER SIM",
        size: 150,
        accessorFn: ({nameSimilarity}) => formatSimilarity(nameSimilarity)
    },
    {
        id: "addressSimilarity",
        header: "ADDRESS SIM",
        size: 150,
        accessorFn: ({addressSimilarity}) => formatSimilarity(addressSimilarity)
    },
    {
        id: "distanceMeters",
        header: "DISTANCE",
        size: 150,
        accessorFn: ({distanceMeters}) => formatDistance(distanceMeters)
    },
];

const describeManualLink = (recordId, {user, updateTime, notes}) =>
    `${recordId} linked manually by ${user} on ${convertValueToCentral(updateTime).format("YYYY-MM-DD HH:mm")} CST: ${notes}`;

const buildEvidenceColumns = (manualLinks) => [
    ...EVIDENCE_COLUMNS,
    {
        id: "manualLink",
        header: "LINKED",
        size: 150,
        cell: ({row}) => {
            const manualRecordIds = [row.original.leftSourceRecordId, row.original.rightSourceRecordId]
                .filter(recordId => manualLinks.has(recordId));
            if (!manualRecordIds.length) return '—';
            return <span className={"linkage-manual-badge"} data-testid={"linkage-manual-badge"}
                         title={manualRecordIds.map(recordId => describeManualLink(recordId, manualLinks.get(recordId))).join('\n')}>
                MANUAL
            </span>;
        }
    },
];

const evidenceTrProps = (row) => ({
    className: classnames("rt-tr", row.index % 2 === 0 ? "-odd" : "-even", "linkage-evidence-row"),
    "data-testid": `linkage-evidence-row-${evidenceRowId(row.original)}`
});

const memberTrProps = (row) => ({
    className: classnames("rt-tr", row.index % 2 === 0 ? "-odd" : "-even", "linkage-member-row",
        {winner: row.original.isWinner}),
    "data-testid": `linkage-member-row-${row.original.id}`
});

const StoreLinkagePage = () => {
    const canonicalStoreId = getStringParamOrDefault("canonicalStoreId", undefined);
    const storeName = getStringParamOrDefault("storeName", undefined);

    const [linkage, setLinkage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [unlinkMember, setUnlinkMember] = useState(null);
    const [linkMember, setLinkMember] = useState(null);
    const [manualLinks, setManualLinks] = useState(new Map());
    const [manualLinksError, setManualLinksError] = useState(null);
    const [manualLinksLoading, setManualLinksLoading] = useState(false);
    const manualLinksRequest = useRef(0);
    const [salesHistory, setSalesHistory] = useState(new Map());
    const [salesHistoryError, setSalesHistoryError] = useState(null);
    const [salesHistoryLoading, setSalesHistoryLoading] = useState(false);
    const salesHistoryRequest = useRef(0);

    const hasRequiredParams = !!canonicalStoreId;

    const loadManualLinks = useCallback((storeLinkage) => {
        const request = ++manualLinksRequest.current;
        const isLatest = () => request === manualLinksRequest.current;
        setManualLinks(new Map());
        setManualLinksError(null);
        setManualLinksLoading(!!storeLinkage);
        if (!storeLinkage) return;
        manualLinksForStore({version: storeLinkage.version, canonicalStoreId: storeLinkage.canonicalStoreId})
            .then(manualLinksFound => isLatest() && setManualLinks(manualLinksFound))
            .catch(() => isLatest() && setManualLinksError('Unable to load manual links; the LINKED column may be incomplete.'))
            .finally(() => isLatest() && setManualLinksLoading(false));
    }, []);

    const loadSalesHistory = useCallback((storeLinkage) => {
        const request = ++salesHistoryRequest.current;
        const isLatest = () => request === salesHistoryRequest.current;
        setSalesHistory(new Map());
        setSalesHistoryError(null);
        setSalesHistoryLoading(!!storeLinkage);
        if (!storeLinkage) return;
        salesHistoryForStore({version: storeLinkage.version, canonicalStoreId: storeLinkage.canonicalStoreId})
            .then(salesHistoryFound => isLatest() && setSalesHistory(salesHistoryFound))
            .catch(() => isLatest() && setSalesHistoryError(SALES_HISTORY_ERROR))
            .finally(() => isLatest() && setSalesHistoryLoading(false));
    }, []);

    const loadLinkage = useCallback(() => {
        setLoading(true);
        setError(null);
        makeApolloClientCall(STORE_LINKAGE_QUERY, false, {canonicalStoreId}).then(response => {
            setLinkage(response.storeLinkage);
            loadManualLinks(response.storeLinkage);
            loadSalesHistory(response.storeLinkage);
        }).catch(() => {
            setLinkage(null);
            setError('Unable to load the linked source records for this store.');
        }).finally(() => setLoading(false));
    }, [canonicalStoreId, loadManualLinks, loadSalesHistory]);

    useEffect(() => {
        if (hasRequiredParams) loadLinkage();
    }, [hasRequiredParams, loadLinkage]);

    const summaryFields = linkage && [
        {label: "Version", value: linkage.version},
        {label: "Sources", value: linkage.sourceCount},
        {label: "Records", value: linkage.memberCount},
        {label: "Winning Source", value: linkage.winningSource},
        {label: "Winning Store ID", value: linkage.winningSourceStoreId},
        {label: "Banner", value: linkage.bannerName},
        {label: "Address", value: linkage.address},
        {label: "City State Zip", value: formatCityStateZip(linkage)},
        {label: "Open Date (earliest)", value: linkage.openDate},
        {label: "Closed Date", value: linkage.closedDate},
    ];

    const members = useMemo(() => linkage?.members ?? [], [linkage]);
    const canUnlink = members.length > 1;

    const memberColumns = useMemo(() => [
        {
            id: "actions", header: "Actions", size: 80,
            cell: ({row}) => <div className={"linkage-action-buttons"}>
                {canUnlink && <button type={"button"} className={"linkage-unlink-button"} data-testid={"react-table-unlink-button"}
                                      onClick={() => setUnlinkMember(row.original)}>
                    <IconImage iconType={Icons.UNLINK} customClass={'unlink-icon'}/>
                </button>}
                <button type={"button"} className={"linkage-link-button"} data-testid={"react-table-link-button"}
                        onClick={() => setLinkMember(row.original)}>
                    <IconImage iconType={Icons.LINK} customClass={'link-icon'}/>
                </button>
            </div>
        },
        ...MEMBER_COLUMNS,
    ], [canUnlink]);

    const matchEvidence = useMemo(() => linkage?.matchEvidence ?? [], [linkage]);
    const evidenceRows = useMemo(() => [
        ...(linkage?.evidenceTruncated ? [] : matchEvidence),
        ...manualRowsWithoutEvidence({members, matchEvidence, manualLinks}),
    ], [linkage, members, matchEvidence, manualLinks]);
    const evidenceColumns = useMemo(() => buildEvidenceColumns(manualLinks), [manualLinks]);
    const salesAnchors = useMemo(
        () => buildSalesAnchorRows(linkage?.salesAnchors,
            {openDate: linkage?.openDate, closedDate: linkage?.closedDate}),
        [linkage]);

    const onAnchorEditConfirmed = ({year, acvAnchor}) =>
        makeApolloClientMutationCall(UPDATE_SALES_ANCHOR_MUTATION, {
            canonicalStoreId: linkage.canonicalStoreId,
            year,
            acvAnchor,
        }).then(response => {
            const {created, salesAnchors: updatedAnchors} = response.updateSalesAnchor;
            setLinkage(current => ({...current, salesAnchors: updatedAnchors}));
            toastSuccess(toast, `${created ? 'Added' : 'Updated'} the ${year} ACV anchor for ${linkage.canonicalStoreId}`);
        }).catch(anchorError => {
            toastError(toast, `Failed to update the ${year} ACV anchor for ${linkage.canonicalStoreId}: ${anchorError.message}`);
            throw anchorError;
        });

    const renderSalesAnchors = () => {
        if (!salesAnchors.length) {
            return <div className={"store-linkage-empty"} data-testid={"store-linkage-no-anchors"}>
                No sales anchors for this store.
            </div>;
        }
        return <div data-testid={"store-linkage-anchors-table"}>
            <EditableReactTableV2
                className={"linkage-anchors-table"}
                data={salesAnchors}
                columns={ANCHOR_COLUMNS}
                editTable={[
                    {id: 'year', Component: args => <EditFormNumberInputField {...args} disabled/>},
                    {id: 'acvAnchor', Component: args => <EditFormNumberInputField {...args} isRequired/>},
                ]}
                editModalAdditionalClass={"linkage-anchor-edit-modal"}
                actionButtonsProps={{showAddButton: false, showDeleteButton: false, showDeleteAllButton: false}}
                initialState={{sorting: [{id: 'year', desc: true}]}}
                showPagination={false}
                hasStickyHeader={false}
                getTrProps={anchorTrProps}
                validatePreSave={validateAnchorPreSave}
                onRowEditConfirmed={onAnchorEditConfirmed}
            />
        </div>;
    };

    const evidenceNotice = () => {
        if (linkage.evidenceTruncated) {
            return <div className={"store-linkage-empty"} data-testid={"store-linkage-evidence-truncated"}>
                This store links too many source records to compare them pairwise.
            </div>;
        }
        if (!matchEvidence.length) {
            return <div className={"store-linkage-empty"} data-testid={"store-linkage-no-evidence"}>
                No stored match evidence for these records.
            </div>;
        }
        return null;
    };

    const renderEvidence = () => {
        if (!linkage.evidenceTruncated && linkage.memberCount <= 1) {
            return <div className={"store-linkage-empty"} data-testid={"store-linkage-single-member"}>
                This store has a single source record; there is nothing to compare.
            </div>;
        }
        if (!evidenceRows.length) {
            return evidenceNotice();
        }
        return <>
            {evidenceNotice()}
            <LoadingWrapper loading={manualLinksLoading} spinnerClassName={'spinner-container-medium'}>
                <div data-testid={"store-linkage-evidence-table"}>
                    <ReactTable
                        className={"linkage-evidence-table"}
                        data={evidenceRows}
                        columns={evidenceColumns}
                        showPagination={false}
                        getRowId={evidenceRowId}
                        getTrProps={evidenceTrProps}
                    />
                </div>
            </LoadingWrapper>
        </>;
    };

    const pageName = storeName || linkage?.bannerName || 'Store Linkage';

    return (
        <Page name={pageName}
              secondaryLabel={linkage?.canonicalStoreId}
              breadCrumbs={<Breadcrumbs items={[{label: "Projections"},
                  {label: "Projection Maintenance", to: "/projectionMaintenance"},
                  {label: "Store Linkage"}]}/>}>
            {!hasRequiredParams &&
                <div className={"store-linkage-empty"} data-testid={"store-linkage-missing-params"}>
                    A canonical store id is required to look up a store linkage.
                </div>}
            {hasRequiredParams && <LoadingWrapper loading={loading}>
                {error && <div className={"store-linkage-empty"} data-testid={"store-linkage-error"}>{error}</div>}
                {!error && !linkage &&
                    <div className={"store-linkage-empty"} data-testid={"store-linkage-not-found"}>
                        No linkage found for canonical store {canonicalStoreId}.
                    </div>}
                {!error && linkage && <>
                    <div className={"store-info-section"} data-testid={"store-linkage-summary"}>
                        {summaryFields.map(field => (
                            <div className={"store-info-field"} key={field.label}>
                                <div className={"store-info-field-label"}>{field.label}</div>
                                <div className={"store-info-field-value"} data-testid={toFieldTestId(field.label)}>
                                    {formatValue(field.value)}
                                </div>
                            </div>
                        ))}
                    </div>
                    <h6 className={"linkage-members-heading"}>Linked Source Records</h6>
                    <div className={"store-linkage-caveat"}>
                        Open date is the earliest across these records and the location is their centroid, so neither is
                        attributed to a single source.
                    </div>
                    <div data-testid={"store-linkage-members-table"}>
                        <ReactTable
                            className={"linkage-members-table"}
                            data={members}
                            columns={memberColumns}
                            showPagination={false}
                            getRowId={(member) => member.id}
                            getTrProps={memberTrProps}
                        />
                    </div>
                    {unlinkMember && <UnlinkSourceStoreModal
                        version={linkage.version}
                        canonicalStoreId={linkage.canonicalStoreId}
                        member={unlinkMember}
                        onClose={() => setUnlinkMember(null)}
                        onUnlinked={() => {
                            setUnlinkMember(null);
                            loadLinkage();
                        }}/>}
                    {linkMember && <LinkSourceStoreModal
                        version={linkage.version}
                        canonicalStoreId={linkage.canonicalStoreId}
                        member={linkMember}
                        onClose={() => setLinkMember(null)}
                        onLinked={() => {
                            setLinkMember(null);
                            loadLinkage();
                        }}/>}
                    <h6 className={"linkage-anchors-heading"}>Sales Anchors</h6>
                    {renderSalesAnchors()}
                    <h6 className={"linkage-sales-history-heading"}>Sales History</h6>
                    <StoreLinkageSalesHistory members={members}
                                              salesHistory={salesHistory}
                                              loading={salesHistoryLoading}
                                              error={salesHistoryError}/>
                    <h6 className={"linkage-evidence-heading"}>Why these records were matched</h6>
                    {manualLinksError && <div className={"store-linkage-caveat"}
                                              data-testid={"store-linkage-manual-links-error"}>{manualLinksError}</div>}
                    {renderEvidence()}
                </>}
            </LoadingWrapper>}
        </Page>
    );
};

export default StoreLinkagePage;
